import {reactive}              from "vue";
import FolderItem              from "./Entities/FolderItem"
import MailboxController       from './MailboxController';
import FoldersHTTPService      from './HTTP/FoldersHTTPService';
import ViewportController      from "./ViewportController";
import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import Language                from "@/js/Shared/Language/Language"
import Controller              from "@/js/Shared/Controllers/Controller";
import BroadcastEventManager   from "@/js/Shared/Events/Managers/BroadcastEventManager";
import SAPOMailConfig          from "@/SAPOMailConfig";

export { FoldersController as default };

/**
* Folders Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class FoldersControllerSingleton extends Controller {
  /**
   * Mailbox Controller
   *
   * @public
   *
   * @type {MailboxController}
   *
   * @since 7.0.0
   */
  mailboxes = MailboxController;

  /**
   * Mailbox Controller
   *
   * @public
   *
   * @type {BroadcastEventManager}
   *
   * @since 7.0.0
   */
  broadcaster = new BroadcastEventManager();

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   */
  constructor() {
    super();
    this.createMainFolders();
  }

  /**
   * This creates a dummy list of all main folders without any data
   *
   * This creates an ilusion that main folders have already been fetched
   * while fetching data in the background
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  createMainFolders() {
    let main_folders = SAPOMailConfig.client.folders.main;
    let data         =  {'a' : []}

    for (let folder in main_folders) {
      data.a.push({
        m : folder,
        l : Language.say('folders.labels.' + main_folders[folder]),
      })
    }

    this.mailboxes.folders_mapping.mapFolders(this.mailboxes.folders, data);
  }

  /**
   * Fetches main folders in the background
   *
   * Prevents main folders from being fetched twice
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise.<void>}
   */
  async fetchMain() {
    this.section_state.setLoading('fetch_all');
    let response = await FoldersHTTPService.fetchMain();
    this.mailboxes.handleResponse(response);
    this.section_state.setLoaded('fetch_all', response.success);
    return response.success;
  }

  /**
   * Fetches main folders in the background
   *
   * Prevents main folders from being fetched twice
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise.<void>}
   */
  async fetchAll() {
    this.section_state.setLoading('fetch_all');
    let response = await FoldersHTTPService.fetchAll();
    this.mailboxes.handleResponse(response);
    this.section_state.setLoaded('fetch_all', response.success);

    return response.success;
  }

  /**
   * Fetches main folders in the background
   *
   * Prevents main folders from being fetched twice
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise.<void>}
   */
  async fetchBackground() {
    this.section_state.setLoading('fetch_background');
    let response = await FoldersHTTPService.fetchAll();
    this.mailboxes.handleResponse(response);
    this.section_state.setLoaded('fetch_background', response.success, false);
    return response.success;
  }

  /**
   * Creates a new folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}          folder_name   folder name
   * @param {FolderItem|null} parent_folder parent folder entity, or null for root folder
   *
   * @return {Promise<Boolean>}
   */
  async create(folder_name, parent_folder) {
    this.saving_state.setSaving('folders_create');
    let response = await FoldersHTTPService.create(folder_name, parent_folder);
    this.mailboxes.handleResponse(response);

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.folders_create_success', [folder_name]);
      this.collection_state.setLastAdded(this.mailboxes.folders.all.getLastAdded());
    }

    this.saving_state.unsetSaving('folders_create');
    return response.success;
  }

  /**
   * Renames an existing folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderItem} folder   folder to rename
   * @param {String}     new_name new folder name
   *
   * @return {Promise<Boolean>}
   */
  async edit(folder, new_name) {
    this.saving_state.setSaving(folder.id);
    let response = await FoldersHTTPService.edit(folder.id, new_name);
    this.mailboxes.handleResponse(response);

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.folders_edit_success', [new_name]);
      this.collection_state.setLastUpdated(
        this.mailboxes.folders.folder_switch,
        this.mailboxes.folders.all.getLastDeleted(),
      );
    }

    this.saving_state.unsetSaving(folder.id);
    return response.success;
  }

  /**
   * Polls a folder to refresh it's indexes
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id   folder to rename
   *
   * @return {Promise<Boolean>}
   */
  async poll(folder_id) {
    let response = await FoldersHTTPService.poll(folder_id);
    this.mailboxes.handleResponse(response);
    return response.success;
  }

  /**
   * Permenently delete a folder and all it's subfolders
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderItem} folder
   *
   * @return {Promise<Boolean>}
   */
  async delete(folder) {
    this.saving_state.setSaving(folder.id);
    let response = await FoldersHTTPService.delete(folder.id);
    this.mailboxes.handleResponse(response);

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.folders_delete_success', [folder.name]);
      this.broadcaster.broadcast('folders-deleted', folder);
      this.collection_state.setLastDeleted(
        this.mailboxes.folders.all.getLastDeleted(),
      );
    }

    this.saving_state.unsetSaving(folder.id);
    return response.success;
  }

  /**
   * Moves a folder into another folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderEntity} target_folder target folder to move to
   * @param {FolderEntity} folder        folder beeing moved
   *
   * @return {Promise<Boolean>}
   */
  async move(target_folder, folder) {
    if(this.canMoveFolderTarget(target_folder, folder) === false){
      NotificationsController.addError('notifications.folders_cant_move', [folder.name, target_folder.name]);
      return;
    }

    if (target_folder.id === '_TOPFOLDER_') {
      target_folder.id = null;
    }

    this.saving_state.setSaving('folders_moving');
    let response = await FoldersHTTPService.move(target_folder.id, folder.id, folder.name);
    this.mailboxes.handleResponse(response);

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.folders_move_success', [folder.name, target_folder.name]);
      this.collection_state.setLastUpdated(
        this.mailboxes.folders.folder_switch,
        folder
      );
    }

    this.saving_state.unsetSaving('folders_moving');
    return response.success;
  }

  /**
   * Emptys all messages in an existing folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderItem} folder
   *
   * @return {boolean}
   */
  async empty(folder) {
    await ViewportController.fetchMessagesIndex(folder.id);
    let folder_data = this.mailboxes.viewport.get(folder.id);

    if (folder_data.uidnext <= 0) {
      NotificationsController.addWarning('notifications.folders_empty_already', [folder.name]);
      return true;
    }

    this.saving_state.setSaving('folders_empty');
    let response = await FoldersHTTPService.empty(folder.id, folder_data.uidnext);
    this.saving_state.unsetSaving('folders_empty');

    if (response.success === true) {
      NotificationsController.addSuccess('notifications.folders_empty_success', [folder.name]);
    }

    this.mailboxes.handleResponse(response);
    return true;
  }

  /**
   * Identifies whether a folder can be moved
   * to another
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderItem} target_folder
   * @param {FolderItem} folder
   *
   * @returns {Boolean}
   */
  canMoveFolderTarget(target_folder, folder){
    if (target_folder.main) {
      return false;
    }

    const folders_disabled = this.getDisabledFolders(folder);
    return !(folders_disabled[target_folder.id]);
  }

  /**
   * Retrieves parent folders that may
   * be disabled
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FolderItem} folder
   *
   * @returns {Object.<String, Boolean>}
   */
  getDisabledFolders(folder){
    const folders_disabled = {};
    folders_disabled[folder.id] = true;

    this.mailboxes.folders.custom.items.forEach((folder) => {
      if (folders_disabled[folder.parent]) {
        folders_disabled[folder.id] = true;
      }
    })

    if (folder.parent) {
      folders_disabled[folder.parent] = true
    }

    if(!folder.parent) {
      folders_disabled['_TOPFOLDER_'] = true
    }

    return folders_disabled;
  }
}

/**
 * @type {FoldersController}
 */
const FoldersController = reactive(new FoldersControllerSingleton());